import { Decimal } from 'decimal.js'
import { EDietSharedComponentType } from '../constants/shared'

const BASE_WEIGHT = 100
const BASE_THERMAL_PROCESSING_COEFFICIENT = 100
const DEFAULT_STANDARDIZATION_FACTOR = 1

/**
 * Transforms the snake_case JSON object into a camelCase model object for a Diet Semi Product Component Pivot.
 */
export const DietSemiProductComponentPivot = (
	json: IDietSemiProductComponentPivot,
): IDietSemiProductComponentPivotModel => {
	const {
		component_type,
		net_weight,
		raw_weight,
		weight_after_processing,
		weight_after_processing_ep,
		weight_before_wom,
		wom,
		weight_after_processing_mech_ep,
		weight_unit,
		group_id,
		...rest
	} = json

	const parsedWeightBeforeWom =
		weight_before_wom === null || weight_before_wom === undefined ? null : Number(weight_before_wom)
	const parsedWom = wom === null || wom === undefined ? null : Number(wom)

	return {
		...rest,
		groupId: group_id,
		componentType: component_type,
		netWeight: Number.isNaN(Number(net_weight)) ? 0 : Number(net_weight),
		rawWeight: Number.isNaN(Number(raw_weight)) ? 0 : Number(raw_weight),
		weightAfterProcessing: Number.isNaN(Number(weight_after_processing)) ? 0 : Number(weight_after_processing),
		weightAfterProcessingEp: Number.isNaN(Number(weight_after_processing_ep))
			? 0
			: Number(weight_after_processing_ep),
		weightBeforeWom: Number.isNaN(parsedWeightBeforeWom) ? null : parsedWeightBeforeWom,
		wom: Number.isNaN(parsedWom) ? null : parsedWom,
		weightAfterProcessingMechEp: Number.isNaN(Number(weight_after_processing_mech_ep))
			? 0
			: Number(weight_after_processing_mech_ep),
		weightUnit: weight_unit,
	}
}

/**
 * Transforms a raw diet semi-product component JSON into a structured model.
 */
export const DietSemiProductComponent = (json: IDietSemiProductComponent): IDietSemiProductComponentModel => {
	const {
		name,
		internal_name,
		warehouse_name,
		pivot,
		food_category,
		is_active,
		is_liquid,
		allergens,
		dietary_tags,
		alternatives,
		nutritional_parameters,
		thermal_processing_coefficient,
		mechanical_processing_coefficient,
		standardization_factor,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
		foodCategory: food_category,
		pivot: DietSemiProductComponentPivot(pivot),
		isLiquid: is_liquid,
		allergens: allergens ? allergens.map(DietAllergen) : [],
		alternatives: alternatives ? alternatives.map(DietaryRawMaterialAlternativeToModel) : [],
		dietaryTags: dietary_tags ? dietary_tags.map(DietDietaryTag) : [],
		standardizationFactor: standardization_factor ?? DEFAULT_STANDARDIZATION_FACTOR,
		nutritionalParameters: nutritional_parameters ? nutritional_parameters.map(NutritionalParameterWithValue) : [],
		thermalProcessingCoefficient: thermal_processing_coefficient,
		mechanicalProcessingCoefficient: mechanical_processing_coefficient,
	}
}

/**
 * Transforms a diet semi-product JSON object into a model object.
 */
export const DietSemiProduct = (json: IDietSemiProduct): IDietSemiProductModel => {
	const {
		name,
		standardization_factor,
		internal_name,
		warehouse_name,
		is_active,
		nutritional_parameters,
		allergens,
		components,
		production_stage_assignments,
		raw_material_component_alternatives,
		tpc,
		created_at,
		updated_at,
		wot_ep_has_mismatch,
		wot_ep_has_no_production_stages,
		wot_ep_recipe_attention,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
		standardizationFactor: standardization_factor,
		nutritionalParameters: nutritional_parameters ? nutritional_parameters.map(NutritionalParameterWithValue) : [],
		allergens: allergens.map(DietAllergen),
		components: components.map(DietSemiProductComponent),
		productionStageAssignments: production_stage_assignments
			? production_stage_assignments.map(DietProductionStageAssignment)
			: null,
		rawMaterialComponentAlternatives: raw_material_component_alternatives
			? raw_material_component_alternatives.map(DietarySemiProductAlternativeToModel)
			: [],
		thermalProcessingCoefficient: tpc ?? null,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
		wotEpHasMismatch: wot_ep_has_mismatch,
		wotEpHasNoProductionStages: wot_ep_has_no_production_stages,
		wotEpRecipeAttention: wot_ep_recipe_attention,
	}
}

export const DietSemiProductRelatedMeal = (json: IDietSemiProductRelatedMeal): IDietSemiProductRelatedMealModel => {
	const { id, name, internal_name, meal_kind, meal_category, media } = json

	return {
		id,
		name: LangContentToModel(name),
		internalName: internal_name,
		mealKind: meal_kind,
		mealCategory: meal_category,
		media: media ? media.map(Media) : undefined,
	}
}

export const DietSemiProductRelated = (json: IDietSemiProductRelated): IDietSemiProductRelatedModel => {
	const { meals } = json

	return {
		meals: meals ? meals.map(DietSemiProductRelatedMeal) : [],
	}
}

/**
 * Transforms a diet semi-product details JSON object into a model object.
 */
export const DietSemiProductDetails = (json: IDietSemiProductDetails): IDietSemiProductDetailsModel => {
	const { dietary_tags, dietary_groups } = json

	const general = DietSemiProduct(json)

	return {
		...general,
		dietaryGroups: dietary_groups ? dietary_groups.map(DietDietaryGroup) : [],
		dietaryTags: dietary_tags ? dietary_tags.map(DietDietaryTag) : [],
	}
}

/**
 * Forms and inside models
 */

/**
 * Converts a diet semi-product component model to a form model.
 */
export const DietSemiProductComponentToFormModel = (
	model: IDietSemiProductComponentModel,
	thermalProcessingCoefficientForAllComponents?: number | null,
): IDietSemiProductComponentFormModel => {
	const {
		pivot,
		name,
		internalName,
		type,
		thermalProcessingCoefficient,
		nutritionalParameters,
		allergens,
		alternatives,
		isLiquid,
		...rest
	} = model

	const {
		groupId,
		rawWeight,
		netWeight,
		weightUnit,
		weightAfterProcessing,
		weightAfterProcessingEp,
		weightBeforeWom,
		wom,
		weightAfterProcessingMechEp,
	} = pivot

	const forAllCoefficient = thermalProcessingCoefficientForAllComponents || null

	// WOT odtwarzamy z ilorazu wag (pivot nie ma kolumny WOT) — 2 miejsca, żeby nie gubić wpisanych wartości
	const difference =
		forAllCoefficient ??
		(netWeight > 0 ? Number(new Decimal(weightAfterProcessing).div(netWeight).times(100).toFixed(2)) : 100)

	const differenceEp =
		netWeight > 0 ? Number(new Decimal(weightAfterProcessingEp).div(netWeight).times(100).toFixed(0)) : 100

	const differenceMechEp =
		netWeight > 0 ? Number(new Decimal(weightAfterProcessingMechEp).div(netWeight).times(100).toFixed(0)) : 100

	const internal_name = type === EDietSharedComponentType.SEMIPRODUCT ? name.pl : internalName

	const hasWomFromBackend = wom !== null && wom !== undefined
	const safeWom = hasWomFromBackend ? wom : 100
	const safeWeightBeforeWom = hasWomFromBackend ? (weightBeforeWom ?? netWeight) : netWeight

	return {
		...rest,
		name,
		internalName: internal_name,
		groupId,
		type,
		weightUnit,
		allergens,
		isLiquid,
		alternatives,
		nutritionalParameters,
		rawWeight,
		netWeight,
		thermalProcessingCoefficient: difference ?? BASE_THERMAL_PROCESSING_COEFFICIENT,
		thermalProcessingCoefficientBeforeUpdate: null,
		weightBeforeWom: safeWeightBeforeWom,
		wom: safeWom,
		weightAfterProcessing: forAllCoefficient
			? Number(new Decimal(netWeight).times(forAllCoefficient).div(100).toFixed(WEIGHT_DECIMAL_PRECISION))
			: weightAfterProcessing,
		weightAfterProcessingEp: differenceEp,
		weightAfterProcessingMechEp: differenceMechEp,
	}
}

/**
 * Converts a raw material model to a semi-product component form model.
 */
export const DietSemiProductSearchComponentRawMaterialToFormModel = (
	model: IDietComponentsRawMaterialModel,
	thermalProcessingCoefficientForAllComponents?: number | null,
): IDietSemiProductComponentFormModel => {
	const { unit, name, internalName, allergens, thermalProcessingCoefficient, isLiquid, alternatives, ...rest } = model

	const rawCoefficient = Number.isNaN(thermalProcessingCoefficient)
		? BASE_THERMAL_PROCESSING_COEFFICIENT
		: Number(thermalProcessingCoefficient)

	const thermal_processing_coefficient = thermalProcessingCoefficientForAllComponents ?? rawCoefficient

	const weightAfterProcessing = BASE_WEIGHT * (thermal_processing_coefficient / 100)

	return {
		...rest,
		name,
		internalName,
		groupId: null,
		type: EDietSharedComponentType.MATERIAL,
		weightUnit: unit,
		netWeight: BASE_WEIGHT,
		rawWeight: BASE_WEIGHT,
		weightAfterProcessing,
		weightAfterProcessingEp: 0,
		weightAfterProcessingMechEp: 0,
		standardizationFactor: DEFAULT_STANDARDIZATION_FACTOR,
		thermalProcessingCoefficient: thermal_processing_coefficient,
		thermalProcessingCoefficientBeforeUpdate: null,
		weightBeforeWom: BASE_WEIGHT,
		wom: 100,
		allergens: allergens ?? [],
		dietaryTags: [],
		alternatives: alternatives ?? [],
		isLiquid,
	}
}

/**
 * Converts a diet material model to a diet semi-product component form model.
 */
export const DietSemiProductSearchComponentMaterialToFormModel = (
	model: IDietMaterialModel,
	thermalProcessingCoefficientForAllComponents?: number | null,
): IDietSemiProductComponentFormModel => {
	const { unit, name, internalName, allergens, thermalProcessingCoefficient, isLiquid, alternatives, ...rest } = model

	const _thermalProcessingCoefficient =
		thermalProcessingCoefficientForAllComponents ??
		thermalProcessingCoefficient ??
		BASE_THERMAL_PROCESSING_COEFFICIENT

	return {
		...rest,
		name,
		internalName,
		groupId: null,
		type: EDietSharedComponentType.MATERIAL,
		weightUnit: unit,
		isLiquid,
		netWeight: BASE_WEIGHT,
		rawWeight: BASE_WEIGHT,
		weightAfterProcessing: BASE_WEIGHT,
		weightAfterProcessingEp: 0,
		weightAfterProcessingMechEp: 0,
		standardizationFactor: DEFAULT_STANDARDIZATION_FACTOR,
		thermalProcessingCoefficient: _thermalProcessingCoefficient,
		thermalProcessingCoefficientBeforeUpdate: null,
		weightBeforeWom: BASE_WEIGHT,
		wom: 100,
		allergens: allergens ?? [],
		dietaryTags: [],
		alternatives: alternatives ?? [],
	}
}

const resolveWomWeights = (
	pivot: { netWeight: number; weightBeforeWom?: number | null; wom?: number | null },
	standardizationFactor: number,
): { netWeight: number; weightBeforeWom: number; wom: number } => {
	const { wom, weightBeforeWom } = pivot

	const netWeight = Number.isNaN(Number(pivot.netWeight))
		? BASE_WEIGHT
		: new Decimal(pivot.netWeight).times(standardizationFactor).toNumber()

	if (wom === null || wom === undefined) {
		return { netWeight, wom: 100, weightBeforeWom: netWeight }
	}

	if (weightBeforeWom === null || weightBeforeWom === undefined) {
		return { netWeight, wom, weightBeforeWom: netWeight }
	}

	return { netWeight, wom, weightBeforeWom: new Decimal(weightBeforeWom).times(standardizationFactor).toNumber() }
}

/**
 * Converts a Diet Component Semi Product Model to a Diet Semi Product Component Form Model.
 */
const DietSemiProductSearchComponentSemiProductComponentToFormModel = (
	model: IDietComponentsSemiProductComponentModel,
	standardizationFactor: number = DEFAULT_STANDARDIZATION_FACTOR,
): IDietSemiProductComponentFormModel => {
	const { id, pivot, name, internalName, type, nutritionalParameters, isLiquid } = model

	const {
		weightUnit,
		rawWeight,
		netWeight,
		weightAfterProcessing,
		weightAfterProcessingEp,
		groupId,
		weightBeforeWom,
		wom,
		weightAfterProcessingMechEp,
	} = pivot

	const thermalProcessingCoefficient =
		netWeight > 0 ? Number(new Decimal(weightAfterProcessing).div(netWeight).times(100).toFixed(2)) : 100

	const internal_name = type === EDietSharedComponentType.SEMIPRODUCT ? name.pl : internalName

	const {
		netWeight: scaledNetWeight,
		wom: safeWom,
		weightBeforeWom: safeWeightBeforeWom,
	} = resolveWomWeights({ netWeight, weightBeforeWom, wom }, standardizationFactor)

	return {
		id,
		name,
		type,
		weightUnit,
		nutritionalParameters,
		isLiquid,
		weightBeforeWom: safeWeightBeforeWom,
		wom: safeWom,
		internalName: internal_name,
		groupId: groupId ?? null,
		standardizationFactor: DEFAULT_STANDARDIZATION_FACTOR,
		thermalProcessingCoefficient: thermalProcessingCoefficient ?? BASE_THERMAL_PROCESSING_COEFFICIENT,
		thermalProcessingCoefficientBeforeUpdate: null,
		rawWeight: Number.isNaN(Number(rawWeight))
			? BASE_WEIGHT
			: new Decimal(rawWeight).times(standardizationFactor).toNumber(),
		netWeight: scaledNetWeight,
		weightAfterProcessing: Number.isNaN(Number(weightAfterProcessing))
			? BASE_WEIGHT
			: new Decimal(weightAfterProcessing).times(standardizationFactor).toNumber(),
		weightAfterProcessingEp: Number.isNaN(Number(weightAfterProcessingEp))
			? 0
			: new Decimal(weightAfterProcessingEp).times(standardizationFactor).toNumber(),
		weightAfterProcessingMechEp: Number.isNaN(Number(weightAfterProcessingMechEp))
			? 0
			: new Decimal(weightAfterProcessingMechEp).times(standardizationFactor).toNumber(),
		allergens: [],
		dietaryTags: [],
	}
}

export const DietSemiProductSearchComponentSemiProductToFormModel = (
	model: IDietComponentsSemiProductModel,
	thermalProcessingCoefficientForAllComponents?: number | null,
): IDietSemiProductComponentFormModel => {
	const { name, dietaryGroups, components, allergens, dietaryTags, standardizationFactor, ...rest } = model

	const weightUnit = components[0]?.pivot.weightUnit

	const netWeight = components.reduce((acc, curr) => acc + Number(curr.pivot.netWeight), 0)
	const rawWeight = components.reduce((acc, curr) => acc + Number(curr.pivot.rawWeight), 0)
	const weightAfterProcessing = components.reduce((acc, curr) => acc + Number(curr.pivot.weightAfterProcessing), 0)
	const weightAfterProcessingEp = components.reduce(
		(acc, curr) => acc + Number(curr.pivot.weightAfterProcessingEp),
		0,
	)
	const weightAfterProcessingMechEp = components.reduce(
		(acc, curr) => acc + Number(curr.pivot.weightAfterProcessingMechEp),
		0,
	)

	const calculatedCoefficient =
		netWeight > 0
			? Number(new Decimal(weightAfterProcessing).div(netWeight).times(100).toFixed(2))
			: BASE_THERMAL_PROCESSING_COEFFICIENT

	const thermalProcessingCoefficient = thermalProcessingCoefficientForAllComponents ?? calculatedCoefficient

	const scaledNetWeight = Number.isNaN(netWeight)
		? BASE_WEIGHT
		: Number(new Decimal(netWeight).times(standardizationFactor).toFixed(2))

	return {
		...rest,
		name,
		internalName: name.pl,
		groupId: null,
		type: EDietSharedComponentType.SEMIPRODUCT,
		weightUnit,
		standardizationFactor,
		rawWeight: Number.isNaN(rawWeight)
			? BASE_WEIGHT
			: Number(new Decimal(rawWeight).times(standardizationFactor).toFixed(2)),
		netWeight: scaledNetWeight,
		thermalProcessingCoefficient,
		thermalProcessingCoefficientBeforeUpdate: null,
		weightBeforeWom: scaledNetWeight,
		wom: 100,
		weightAfterProcessing: Number.isNaN(weightAfterProcessing)
			? BASE_WEIGHT
			: Number(new Decimal(weightAfterProcessing).times(standardizationFactor).toFixed(2)),
		weightAfterProcessingEp: Number.isNaN(weightAfterProcessingEp)
			? 0
			: Number(new Decimal(weightAfterProcessingEp).times(standardizationFactor).toFixed(2)),
		weightAfterProcessingMechEp: Number.isNaN(weightAfterProcessingMechEp)
			? 0
			: Number(new Decimal(weightAfterProcessingMechEp).times(standardizationFactor).toFixed(2)),
		allergens: allergens ?? [],
		dietaryTags: dietaryTags ?? [],
		components: components.map(c => DietSemiProductSearchComponentSemiProductComponentToFormModel(c)),
		groups: dietaryGroups ? dietaryGroups.map((d, i) => DietDietaryGroupModelToForm(d, i + 1)) : [],
	}
}

export const DietSemiProductDetailsToFormModel = (
	model: IDietSemiProductDetailsModel,
): IDietSemiProductComponentFormModel => {
	const { id, name, type, components, standardizationFactor, nutritionalParameters, dietaryGroups } = model
	const { thermalProcessingCoefficient } = model

	return {
		id,
		type,
		name,
		nutritionalParameters,
		standardizationFactor,
		internalName: name.pl,
		groupId: null,
		weightUnit: BASE_WEIGHT_UNIT,
		netWeight: BASE_WEIGHT,
		rawWeight: BASE_WEIGHT,
		weightAfterProcessing: BASE_WEIGHT,
		weightAfterProcessingEp: 0,
		weightAfterProcessingMechEp: 0,
		thermalProcessingCoefficient: BASE_THERMAL_PROCESSING_COEFFICIENT,
		thermalProcessingCoefficientBeforeUpdate: null,
		weightBeforeWom: BASE_WEIGHT,
		wom: 100,
		allergens: [],
		dietaryTags: [],
		components: components
			? components.map(component => DietSemiProductComponentToFormModel(component, thermalProcessingCoefficient))
			: [],
		groups: dietaryGroups ? dietaryGroups.map((d, i) => DietDietaryGroupModelToForm(d, i + 1)) : [],
	}
}

/**
 * Payloads
 */
const DietSemiProductComponentToPayload = (
	component: IDietSemiProductComponentFormModel,
	alternative: IDietaryAlternativeMaterialManage[],
): IDietSemiProductComponentPayload => {
	const { id, type, groupId, weightUnit, netWeight, weightAfterProcessing, weightBeforeWom, wom } = component

	return {
		id,
		type,
		wom,
		internal_group_id: groupId,
		weight_unit: weightUnit,
		net_weight: netWeight,
		raw_weight: weightAfterProcessing,
		weight_after_processing: weightAfterProcessing,
		weight_before_wom: weightBeforeWom,
		alternatives: alternative.map(DietaryAlternativeMaterialManageToSemiProductPayload),
	}
}

const DietSemiProductSharedToPayload = (
	event: IFormSubmitEvent<IDietSemiProductForm>,
	others: IDietSemiProductPayloadOptions,
): IDietSemiProductPayloadShared => {
	const { name, warehouseName, isWarehouseNameSameAsInternalName } = event.values

	const {
		allergens,
		dietaryTags,
		dietaryGroups,
		components,
		productionStages,
		alternativeMaterials,
		thermalProcessingCoefficient,
		isThermalProcessingCoefficientForAllComponents,
	} = others

	const groupIdsFromComponents: number[] = []
	const _components = components.flatMap(component => {
		const alternativeForComponent = alternativeMaterials.filter(
			material => material.originalMaterialId === component.id && material.groupId === null,
		)

		if (!component.groupId) return [DietSemiProductComponentToPayload(component, alternativeForComponent)]

		groupIdsFromComponents.push(component.groupId)

		return (
			component.components?.map(nestedComponent => {
				const alternativeForGroupComponent = alternativeMaterials.filter(
					material =>
						material.originalMaterialId === nestedComponent.id &&
						material.groupId === nestedComponent.groupId,
				)

				return DietSemiProductComponentToPayload(nestedComponent, alternativeForGroupComponent)
			}) ?? []
		)
	})

	const groups: IDietDietaryGroupPayload[] = dietaryGroups
		.filter(g => groupIdsFromComponents.includes(g.id))
		.map(DietDietaryGroupToPayload)

	return {
		groups,
		warehouse_name: isWarehouseNameSameAsInternalName ? name : warehouseName,
		allergens: allergens ? allergens.map(allergen => allergen.id) : [],
		dietary_tags: dietaryTags ? dietaryTags.map(tag => tag.value) : [],
		components: _components,
		production_stages: productionStages.flatMap(DietProductionStageToPayload),
		tpc: isThermalProcessingCoefficientForAllComponents
			? ((Number.isNaN(thermalProcessingCoefficient) ? null : Number(thermalProcessingCoefficient)) ?? null)
			: null,
	}
}

export const DietSemiProductUpdateToPayload = (
	updated: IDietSemiProductDetailsModel,
	event: IFormSubmitEvent<IDietSemiProductForm>,
	others: IDietSemiProductPayloadOptions,
): IDietSemiProductUpdatePayload => {
	const { name } = event.values

	const shared = DietSemiProductSharedToPayload(event, others)

	return {
		...shared,
		id: updated.id,
		name: LangContentToJson(updated.name, name),
		is_active: updated.isActive,
	}
}

export const DietSemiProductCreateToPayload = (
	event: IFormSubmitEvent<IDietSemiProductForm>,
	others: IDietSemiProductPayloadOptions,
): IDietSemiProductCreatePayload => {
	const { name } = event.values

	const shared = DietSemiProductSharedToPayload(event, others)

	return {
		...shared,
		name: generateLangContentArray(name),
		is_active: true,
	}
}
